import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { Theme } from 'hephaestus/core';
import { Box, Typography } from 'hephaestus/web';
import {
  BLOTTER_BADGE_SX,
  BOLD_SX,
  LABEL_UPPER_SX,
  MICRO_SX,
  SECONDARY_TEXT_SX,
  SECTION_HEADER_SX,
  SORTABLE_HEADER_SX,
} from '../styles';
import { tradeSideColor } from '../utils';
export const StatPill = ({
  label,
  value,
  color,
  active,
  onClick,
  theme,
}: {
  label: string;
  value: number | string;
  color: string;
  active?: boolean;
  onClick?: () => void;
  theme?: Theme;
}) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      px: 1.5,
      py: 0.75,
      borderRadius: 1,
      bgcolor: active && theme ? theme.color.primaryVariant : 'transparent',
      cursor: onClick ? 'pointer' : 'default',
      '&:hover': onClick && theme ? { bgcolor: theme.color.neutralHover } : {},
      borderLeft: '3px solid',
      borderLeftColor: color,
    }}
  >
    <Typography variant='titleMedium' sx={BOLD_SX}>
      {typeof value === 'number' ? value.toLocaleString() : value}
    </Typography>
    <Typography variant='bodySmall' sx={SECONDARY_TEXT_SX}>
      {label}
    </Typography>
  </Box>
);

export const CollapsibleHeader = ({
  open,
  onClick,
  label,
  badge,
  theme,
}: {
  open: boolean;
  onClick: () => void;
  label: string;
  badge?: React.ReactNode;
  theme: Theme;
}) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      cursor: 'pointer',
      userSelect: 'none',
      flex: 1,
      '&:hover': { color: theme.color.onSurface },
    }}
  >
    <ExpandMoreIcon
      sx={{
        fontSize: 16,
        color: theme.color.onSurfaceVariant,
        transform: open ? 'rotate(180deg)' : 'none',
        transition: 'transform 0.2s',
      }}
    />
    <Typography
      variant='labelSmall'
      sx={{
        ...LABEL_UPPER_SX,
        color: theme.color.onDisabled,
        letterSpacing: '0.05em',
      }}
    >
      {label}
    </Typography>
    {badge}
  </Box>
);

export const SideBadge = ({
  isBuy,
  label,
  theme,
}: {
  isBuy: boolean;
  label: string;
  theme: Theme;
}) => (
  <Box
    sx={{
      display: 'inline-block',
      ...BLOTTER_BADGE_SX,
      bgcolor: isBuy
        ? theme.color.positiveVariant
        : theme.color.negativeVariant,
    }}
  >
    <Typography
      variant='labelSmall'
      sx={{
        ...MICRO_SX,
        ...BOLD_SX,
        color: tradeSideColor(theme.color, isBuy),
      }}
    >
      {label}
    </Typography>
  </Box>
);

export const SortableColumnHeader = ({
  field,
  label,
  sortField,
  sortDir,
  onSort,
  align = 'right',
  theme,
}: {
  field: string;
  label: string;
  sortField: string;
  sortDir: 'asc' | 'desc';
  onSort: (f: string) => void;
  align?: string;
  theme: Theme;
}) => {
  const active = sortField === field;
  return (
    <Box
      onClick={() => onSort(field)}
      sx={{ ...SORTABLE_HEADER_SX, textAlign: align, px: 0.5 }}
    >
      <Typography
        variant='labelSmall'
        component='span'
        sx={{
          ...SECTION_HEADER_SX,
          whiteSpace: 'nowrap',
          color: active ? theme.color.primary : theme.color.onSurfaceVariant,
          fontWeight: active ? 800 : 700,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant='labelSmall'
        component='span'
        sx={{
          ml: 0.25,
          opacity: active ? 1 : 0.3,
          color: active ? theme.color.primary : undefined,
        }}
      >
        {active ? (sortDir === 'asc' ? '▲' : '▼') : '⇕'}
      </Typography>
    </Box>
  );
};

export const ColumnHeader = ({
  label,
  align = 'right',
  color,
  bg,
}: {
  label: string;
  align?: string;
  color?: string;
  bg?: string;
}) => (
  <Box sx={{ textAlign: align, px: 0.5, ...(bg ? { bgcolor: bg } : {}) }}>
    <Typography
      variant='labelSmall'
      sx={{ ...SECTION_HEADER_SX, whiteSpace: 'nowrap', color }}
    >
      {label}
    </Typography>
  </Box>
);

export const TriggerBadge = ({
  label,
  theme,
}: {
  label: string;
  theme: Theme;
}) => {
  const bg =
    label === 'Drift'
      ? theme.color.warningVariant
      : label === 'Liquidate'
        ? theme.color.negativeVariant
        : theme.color.primaryVariant;
  const color =
    label === 'Drift'
      ? theme.color.warning
      : label === 'Liquidate'
        ? theme.color.negative
        : theme.color.primary;
  return (
    <Box sx={{ px: 0.5, py: '1px', borderRadius: '3px', bgcolor: bg }}>
      <Typography
        variant='labelSmall'
        sx={{ ...MICRO_SX, fontWeight: 600, color }}
      >
        {label}
      </Typography>
    </Box>
  );
};
